"use client"

import { useTransportOrganizationContext } from "@/transport"
import { useOrganization } from "@clerk/nextjs"

import { BillingStatusBanner } from "../components/BillingStatusBanner"
import { useBillingSubscription } from "../hooks/useBillingSubscription"
import { useBillingUsage } from "../hooks/useBillingUsage"
import { useOpenBillingPortal } from "../hooks/useOpenBillingPortal"

interface BillingStatusBannerRouteProps {
  className?: string
}

/**
 * The billing banner wherever the customer is, rather than only on Home. Owns
 * its own queries so the shell does not have to know about billing: the app
 * shell renders whatever the navigation module contributes here.
 *
 * Both queries are shared through React Query, so mounting this beside a page
 * that reads the same subscription costs one request, not two.
 */
export const BillingStatusBannerRoute = ({
  className,
}: BillingStatusBannerRouteProps) => {
  const { organization } = useOrganization()
  const { isAuthLoaded, isOrganizationSynced, organizationSlug } =
    useTransportOrganizationContext()

  // The billing API resolves the organization from the route, so asking before
  // Clerk and the route agree returns another tenant's usage or a 404.
  const enabled =
    organization != null &&
    isAuthLoaded &&
    isOrganizationSynced &&
    organizationSlug !== null &&
    organization.slug === organizationSlug

  const subscriptionQuery = useBillingSubscription({ enabled })
  const usageQuery = useBillingUsage({ enabled })
  const { openPortal, isPending } = useOpenBillingPortal()

  if (!enabled) {
    return null
  }

  return (
    <BillingStatusBanner
      className={className}
      organizationId={organization?.id ?? null}
      subscription={subscriptionQuery.data ?? null}
      usage={usageQuery.data ?? null}
      onPortalAction={(flow) => {
        openPortal(flow, "shell_banner")
      }}
      isPortalLoading={isPending}
    />
  )
}
